import { useState } from "react";
import { CalendarPlus, CalendarDays, Clock, MapPin, Phone, Navigation } from "lucide-react";
import { downloadIcs, googleCalendarUrl } from "@/lib/calendar";
import { fmtDate, todayISO } from "@/lib/api";
import { EmptyState } from "@/components/Field";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { AppointmentStatus, PaymentBadge } from "@/components/AppointmentStatus";

const StudioInfo = ({ s, testid }) => (
  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#e3d3bf] bg-[#fbf8f4] px-3 py-2.5" data-testid={testid}>
    <div className="min-w-0 text-xs text-slate-600">
      <div className="text-sm font-semibold text-[#0f172a]">{s.name}</div>
      {(s.address || s.city) && <div className="flex items-start gap-1"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{[s.address, [s.cap, s.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>}
      {s.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /><a href={`tel:${s.phone}`} className="hover:underline">{s.phone}</a></div>}
      {s.directions && <div className="mt-0.5 text-slate-500">{s.directions}</div>}
    </div>
    <a href={s.maps_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-maps`}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#b8956f] px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#a3825e]">
      <Navigation className="h-3.5 w-3.5" /> Apri in Google Maps
    </a>
  </div>
);

const AddToCalendar = ({ a, testid }) => (
  <div className="mt-3 flex flex-wrap gap-2" data-testid={testid}>
    <button onClick={(e) => { e.stopPropagation(); downloadIcs(a); }} data-testid={`${testid}-ics`}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#3d655b] px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2f4f47]">
      <CalendarPlus className="h-3.5 w-3.5" /> Aggiungi al calendario
    </button>
    <a href={googleCalendarUrl(a)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-google`}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#3d655b] px-3.5 py-2 text-xs font-semibold text-[#3d655b] transition-colors hover:bg-[#edf2ef]">
      Google Calendar
    </a>
  </div>
);

export function AppointmentList({ items, onClick, prefix = "appt", showCalendar = false }) {
  const today = todayISO();
  return (
    <div className="space-y-3">
      {items.map((a) => (
        <div key={a.id} onClick={() => onClick?.(a)} data-testid={`${prefix}-item-${a.id}`}
          className={`flex items-center gap-4 rounded-2xl border border-[#e2e8f0] bg-white p-4 ${onClick ? "cursor-pointer hover:border-[#b8956f]" : ""} ${a.date < today ? "opacity-60" : ""}`}>
          <div className="w-16 shrink-0 rounded-xl bg-[#edf2ef] py-2 text-center">
            <div className="text-[10px] font-semibold uppercase text-[#2f4f47]">{fmtDate(a.date, { month: "short" })}</div>
            <div className="text-xl font-extrabold leading-none">{fmtDate(a.date, { day: "2-digit" })}</div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><span className="font-semibold">{a.reason || "Visita"}</span><AppointmentStatus status={a.status} testid={`${prefix}-status-${a.id}`} /><PaymentBadge appt={a} testid={`${prefix}-paid-${a.id}`} /></div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.time} · {a.duration} min</span>
              {a.location && !a.studio && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
            </div>
            {a.studio && a.status !== "disdetto" && <StudioInfo s={a.studio} testid={`${prefix}-studio-${a.id}`} />}
            {showCalendar && a.date >= today && a.status !== "disdetto" && <AddToCalendar a={a} testid={`${prefix}-calendar-${a.id}`} />}
          </div>
          {a.date < today && <span className="text-xs font-medium text-slate-400">Passato</span>}
        </div>
      ))}
    </div>
  );
}

export function PatientAppointmentsTab({ rec, reload }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Appuntamenti</h3>
        <button className="btn-primary" onClick={() => { setEdit(null); setOpen(true); }} data-testid="fissa-appuntamento-btn"><CalendarPlus className="h-4 w-4" /> Fissa appuntamento</button>
      </div>
      {rec.appointments.length ? (
        <AppointmentList items={rec.appointments} onClick={(a) => { setEdit(a); setOpen(true); }} prefix="patient-appt" />
      ) : <EmptyState icon={CalendarDays} title="Nessun appuntamento" testid="patient-appointments-empty" />}
      <AppointmentDialog open={open} onOpenChange={setOpen} initial={edit} fixedPatient={rec.patient} onSaved={reload} />
    </div>
  );
}
